import { BpButton } from '../../buttons/bp-button/BpButton'
import { IconArrowLeft } from '../../icons/IconArrowLeft'
import { IconArrowRight } from '../../icons/IconArrowRight'
import { ChangeEvent, useEffect, useState } from 'react'
import { TablePaginationConfig } from 'antd'
import { BpSelect } from '../../forms/select/BpSelect'
import { BpInput } from '../../forms/input/BpInput'
import { useDebounce } from 'use-debounce'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import styles from './Pagination.module.scss'

export interface IBpPaginationProps extends TablePaginationConfig {
  total?: number
  totalRows?: number
  className?: string
  disabled?: boolean
  sizePagination?: 'sm' | 'md'
}

const PAGE_SIZE_OPTIONS = [
  { value: '10', text: '10' },
  { value: '20', text: '20' },
  { value: '50', text: '50' },
  { value: '100', text: '100' },
  { value: '500', text: '500' },
]

export const BpPagination = ({
  pageSize = 10,
  total = 1,
  current = 1,
  totalRows = 1,
  onChange,
  className,
  disabled = false,
  sizePagination: size = 'md',
}: IBpPaginationProps) => {
  const { t } = useTranslation()
  const [currentPageSize, setCurrentPageSize] = useState<number>(pageSize)
  const [currentPage, setCurrentPage] = useState<number>(current)
  const [currentTotal, setCurrentTotal] = useState(total)
  const [debouncedValue] = useDebounce(currentPage, 1000)

  useEffect(() => {
    total <= 0 ? setCurrentTotal(1) : setCurrentTotal(total)
  }, [total])

  useEffect(() => {
    if (currentPage > currentTotal) setCurrentPage(currentTotal)
  }, [currentTotal])

  useEffect(() => {
    if (onChange) onChange(currentPage, currentPageSize)
    if (!debouncedValue) setCurrentPage(1)
  }, [debouncedValue, currentPageSize])

  const handlePrevious = () => {
    const result = currentPage - 1
    setCurrentPage(result <= 0 ? 1 : result)
  }

  const handleNext = () => {
    const result = currentPage + 1
    setCurrentPage(result > currentTotal ? currentTotal : result)
  }

  const handleChangePage = (e: ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value.replace(/\D/, ''))

    if (value <= currentTotal) {
      setCurrentPage(value)
    }
  }
  return (
    <div className={clsx(styles.root, className)}>
      <div className={styles.layout}>
        <p className='whitespace-nowrap text-[15px] font-semibold text-focus-500'>
          {t('table.pagination.ofItems', {
            page: `${currentPage * currentPageSize - currentPageSize + 1}–${
              currentPage * currentPageSize > totalRows ? totalRows : currentPage * currentPageSize
            }`,
            total: totalRows,
          })}
        </p>
        {size === 'md' && (
          <>
            <div className={styles.divider}></div>
            <div className={clsx('flex items-center gap-[8px]', styles.end)}>
              <BpSelect
                hideIcon
                className='w-[102px]'
                defaultValue={pageSize}
                disabled={disabled}
                dropPosition={'auto'}
                options={PAGE_SIZE_OPTIONS}
                showSearch={false}
                value={currentPageSize}
                valueColor='text-focus-500'
                onChange={(e) => setCurrentPageSize(+e.target.value)}
              ></BpSelect>
              <span className='whitespace-nowrap text-[15px] text-focus-500'>
                {t('table.pagination.itemsPerPage')}
              </span>
            </div>
            <div className={clsx(styles.divider, styles.dividerEdge)}></div>
            <div className='flex items-center gap-[8px]'>
              <div className='w-[50px]'>
                <BpInput
                  defaultValue={current}
                  disabled={disabled}
                  max={currentTotal}
                  value={currentPage}
                  onChange={handleChangePage}
                />
              </div>
              <span className='whitespace-nowrap text-[15px] text-focus-500'>
                {t('table.pagination.ofPages', { total: currentTotal })}
              </span>
            </div>
          </>
        )}
        <div className={styles.divider}></div>
        <div data-ai-button-anchor className={clsx('flex items-center gap-[8px]', styles.end)}>
          <BpButton
            disabled={disabled || currentPage <= 1}
            iconLeft={<IconArrowLeft />}
            theme='outline'
            onClick={handlePrevious}
          >
            {t('table.pagination.previous')}
          </BpButton>
          <BpButton
            disabled={disabled || currentPage >= currentTotal}
            iconRight={<IconArrowRight />}
            theme='outline'
            onClick={handleNext}
          >
            {t('table.pagination.next')}
          </BpButton>
        </div>
      </div>
    </div>
  )
}
